import React, { useState, useMemo, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  CircularProgress,
  InputAdornment,
  Tabs,
  Tab,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  GetApp as GetAppIcon,
  FilterList as FilterListIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  AccountCircle as AccountCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterAlt as FilterAltIcon,
  Calculate as CalculateIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ListAlt as ListAltIcon,
  SwapHoriz as SwapHorizIcon,
  AddCircle as AddCircleIcon,
  LocalAtm as LocalAtmIcon,
  CreditCard as CreditCardIcon,
  ReceiptLong as ReceiptLongIcon,
  Payment as PaymentIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { notifyPermissionDenied } from '../../utils/apiPermissionError';
import { useNavigate } from 'react-router-dom';
import {
  matchesSharedModuleRoute,
  navigateWithModuleBack,
} from '../../utils/moduleNavigationContext';
import tesoreriaCajaService, {
  InformacionCaja,
  MovimientoCaja,
  DetalleCajaResponse,
  CajaResumen,
  CajaSearchResponse,
  InformacionArqueo,
  ArqueoPorTipoPago,
  CierreCajaRequest,
  getMovimientoCajaPdfConfig,
  esMovimientoTraspaso,
  formatCajaPlanillaLabel,
  formatNumeroChequeMovimiento,
  movimientoTieneDatosCheque,
} from '../../services/tesoreriaCajaService';
import tesoreriaService, { GrupoCajaChica } from '../../services/tesoreriaService';
import sucursalesVentaService, { SucursalVenta } from '../../services/sucursalesVentaService';
import { formatCurrency, formatDate, formatComprobanteTesoreria } from '../../utils/formatters';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import * as XLSX from 'xlsx';
import PDFViewer from '../../components/shared/PDFViewer';
import ArqueoTipoPagoDetalleDialog from '../../components/Tesoreria/ArqueoTipoPagoDetalleDialog';
import SearchableSelect, { searchableSelectAutocompleteProps } from '../../components/SearchableSelect';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { resolveNroSucFiltro, useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import { applySucursalPreferidaIfNeeded } from '../../utils/sucursalPreferida';
import api from '../../services/api';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';
import { getAccessToken } from '../../utils/sessionAuthStore';

interface MovimientosCajaFiltrosStored {
  fechaDesde?: string;
  fechaHasta?: string;
  searchTerm?: string;
  tipoFiltro?: 'TODOS' | 'INGRESO' | 'EGRESO';
  medioPagoFiltro?: string;
  currentTab?: number;
  mainTab?: number;
  filtroNroSuc?: number | null;
}

const TIPO_FILTRO_CAJA_OPTIONS = [
  { value: 'TODOS', label: 'Todos' },
  { value: 'INGRESO', label: 'Ingresos' },
  { value: 'EGRESO', label: 'Egresos' },
] as const;

const ESTADO_CAJA_OPTIONS = [
  { value: 'ABIERTO', label: 'ABIERTO' },
  { value: 'CERRADO', label: 'CERRADO' },
] as const;

const STORAGE_KEY_CODIGO_CAJA = 'planillaCaja_codigoCaja';

const BOTONERA_ITEMS = [
  { text: 'Recibos de cobranza', icon: ListAltIcon, path: '/tesoreria/recibos' },
  { text: 'Devoluciones', icon: ListAltIcon, path: '/tesoreria/devoluciones' },
  { text: 'Orden de Pago', icon: ListAltIcon, path: '/tesoreria/orden-pago' },
  { text: 'Traspasos', icon: SwapHorizIcon, path: '/tesoreria/transferencias' },
  { text: 'Movimientos Varios', icon: ListAltIcon, path: '/tesoreria/movimientos-varios' },
  { text: 'Movimientos Bancarios', icon: ListAltIcon, path: '/tesoreria/movimientos-bancarios' },
];

type TipoPagoColumnaId =
  | 'EFECTIVO'
  | 'TRANSFERENCIA'
  | 'CHEQUE_TERC'
  | 'CHEQUE_PROP'
  | 'TARJETA'
  | 'OTROS';

/** Misma lógica que CajaController.ResolverIndiceColumnaCierreTipoPago (PDF cierre de caja). */
const resolveTipoPagoColumnaIdFromCodigo = (tipoPago?: string | null): TipoPagoColumnaId => {
  const cod = (tipoPago ?? '').trim().toUpperCase();
  if (cod.startsWith('TB')) return 'TRANSFERENCIA';
  const c0 = cod.charAt(0);
  switch (c0) {
    case 'E':
      return 'EFECTIVO';
    case 'C':
      return 'CHEQUE_TERC';
    case 'P':
      return 'CHEQUE_PROP';
    case 'T':
      return 'TARJETA';
    case 'R':
    case 'O':
    default:
      return 'OTROS';
  }
};

/** Icono y acento para tarjetas de arqueo según tipo de cobro/pago (mismos datos que la tabla por tipo). */
const getArqueoTipoPagoCardPresentation = (
  item: ArqueoPorTipoPago
): { Icon: typeof LocalAtmIcon; accent: string } => {
  const col = resolveTipoPagoColumnaIdFromCodigo(item.tipoPago);
  switch (col) {
    case 'EFECTIVO':
      return { Icon: LocalAtmIcon, accent: '#2e7d32' };
    case 'TARJETA':
      return { Icon: CreditCardIcon, accent: '#1565c0' };
    case 'CHEQUE_TERC':
    case 'CHEQUE_PROP':
      return { Icon: ReceiptLongIcon, accent: '#7b1fa2' };
    case 'TRANSFERENCIA':
      return { Icon: AccountBalanceIcon, accent: '#00838f' };
    default:
      return { Icon: PaymentIcon, accent: '#6d4c41' };
  }
};

/** Listado unificado: fecha desc, luego letra + sucursal + nº comprobante desc. */
const compareMovimientosCajaPorFechaYComprobante = (a: MovimientoCaja, b: MovimientoCaja): number => {
  const dateA = new Date(a.fechaEmision).getTime();
  const dateB = new Date(b.fechaEmision).getTime();
  if (dateA !== dateB) return dateB - dateA;
  const letraCmp = (b.letra ?? '').localeCompare(a.letra ?? '', undefined, { sensitivity: 'base' });
  if (letraCmp !== 0) return letraCmp;
  const sucDiff = (b.nroSuc ?? 0) - (a.nroSuc ?? 0);
  if (sucDiff !== 0) return sucDiff;
  const claveComp = (m: MovimientoCaja) =>
    (m.nroComprobante ?? 0) !== 0 ? (m.nroComprobante ?? 0) : (m.nroFormulario ?? 0);
  return claveComp(b) - claveComp(a);
};

type TipoPagoColumna = {
  id: TipoPagoColumnaId;
  label: string;
};

const TIPOS_PAGO_COLUMNAS: TipoPagoColumna[] = [
  { id: 'EFECTIVO', label: 'Efectivo' },
  { id: 'TRANSFERENCIA', label: 'Transferencias' },
  { id: 'CHEQUE_TERC', label: 'Cheque Terc.' },
  { id: 'CHEQUE_PROP', label: 'Cheque Prop.' },
  { id: 'TARJETA', label: 'Tarjeta' },
  { id: 'OTROS', label: 'Otros' },
];

/** Anchos mínimos (px) y padding horizontal de la grilla de movimientos de caja. */
const GRILLA_MOV_CAJA_COL = {
  fecha: 112,
  tipoEntidad: 168,
  medioCobro: 180,
  cajaTraspaso: 168,
  tipoPago: 112,
  saldo: 116,
  imprimir: 72,
} as const;

const GRILLA_MOV_CAJA_COL_PX = 1.25;

const sxGrillaTextoTruncado = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  lineHeight: 1.3,
  maxWidth: '100%',
} as const;

const GrillaTextoConTooltip: React.FC<{
  texto: string;
  sx?: Record<string, unknown>;
}> = ({ texto, sx }) => {
  const valor = (texto ?? '').trim() || '-';
  const sinTooltip = valor === '-' || valor === '—';
  return (
    <Tooltip title={sinTooltip ? '' : valor} enterDelay={400} disableHoverListener={sinTooltip}>
      <Box component="span" sx={{ ...sxGrillaTextoTruncado, ...sx }}>
        {valor}
      </Box>
    </Tooltip>
  );
};

const sxGrillaMovCajaCol = (
  key: keyof typeof GRILLA_MOV_CAJA_COL,
  extra?: Record<string, unknown>
) => ({
  minWidth: GRILLA_MOV_CAJA_COL[key],
  px: GRILLA_MOV_CAJA_COL_PX,
  boxSizing: 'border-box' as const,
  ...(key === 'imprimir'
    ? { width: GRILLA_MOV_CAJA_COL.imprimir, maxWidth: GRILLA_MOV_CAJA_COL.imprimir }
    : {}),
  ...extra,
});

const calcAnchoMinimoGrillaMovimientos = (cantidadColumnasTipoPago: number): number => {
  let total =
    GRILLA_MOV_CAJA_COL.fecha +
    GRILLA_MOV_CAJA_COL.tipoEntidad +
    GRILLA_MOV_CAJA_COL.medioCobro +
    GRILLA_MOV_CAJA_COL.cajaTraspaso +
    GRILLA_MOV_CAJA_COL.saldo +
    GRILLA_MOV_CAJA_COL.imprimir;
  total += cantidadColumnasTipoPago * GRILLA_MOV_CAJA_COL.tipoPago;
  return total;
};

const getTipoPagoColumnaId = (mov: MovimientoCaja): TipoPagoColumnaId =>
  resolveTipoPagoColumnaIdFromCodigo(mov.tipoPago);

const getArqueoTipoPagoDisplayLabel = (item: ArqueoPorTipoPago): string => {
  const desc = item.descripcion?.trim();
  if (desc) return desc;
  const col = TIPOS_PAGO_COLUMNAS.find((c) => c.id === resolveTipoPagoColumnaIdFromCodigo(item.tipoPago));
  if (col) return col.label;
  return item.tipoPago?.trim() || 'Sin descripción';
};

/**
 * Importe con signo para la columna de medios y el saldo: la API envía el efecto neto sobre la caja
 * (positivo entra, negativo sale; p. ej. vuelto en efectivo como negativo).
 */
const signedImporteDisplay = (mov: MovimientoCaja): number => {
  const raw = Number(mov.importe);
  if (!Number.isFinite(raw)) return 0;
  if (mov.tipoMovimiento?.toUpperCase() === 'INGRESO') return raw < 0 ? raw : Math.abs(raw);
  return raw <= 0 ? raw : -Math.abs(raw);
};

/** Saldo después de cada fila en listado DESC (arranca desde saldoFinal y retrocede en el tiempo). */
const attachSaldoAcumuladoDesc = (
  movimientosDesc: MovimientoCaja[],
  saldoFinal: number,
  movimientosPreviosDesc: MovimientoCaja[] = []
): Array<MovimientoCaja & { saldoAcumulado: number }> => {
  let saldo = saldoFinal;
  movimientosPreviosDesc.forEach(mov => {
    saldo -= signedImporteDisplay(mov);
  });
  return movimientosDesc.map(mov => {
    const row = { ...mov, saldoAcumulado: saldo };
    saldo -= signedImporteDisplay(mov);
    return row;
  });
};

const MovimientosCaja: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [codigoCaja, setCodigoCaja] = useState<number | ''>(() => {
    try {
      const s = localStorage.getItem(STORAGE_KEY_CODIGO_CAJA);
      if (s) {
        const n = parseInt(s, 10);
        if (Number.isFinite(n) && n > 0) return n;
      }
    } catch {}
    return '';
  });
  const [informacionCaja, setInformacionCaja] = useState<InformacionCaja | null>(null);
  const [detallesCaja, setDetallesCaja] = useState<DetalleCajaResponse | null>(null);
  const [arqueoCaja, setArqueoCaja] = useState<InformacionArqueo | null>(null);
  const [grupoNombre, setGrupoNombre] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetalles, setLoadingDetalles] = useState(false);
  const [loadingArqueo, setLoadingArqueo] = useState(false);
  const [movimientosAnteriores, setMovimientosAnteriores] = useState<MovimientoCaja[]>([]);

  const loadStored = () =>
    loadTesoreriaFiltros<MovimientosCajaFiltrosStored>(TESORERIA_FILTROS_KEYS.movimientosCaja);
  const resolveTipoFiltro = (): 'TODOS' | 'INGRESO' | 'EGRESO' => {
    const value = loadStored().tipoFiltro;
    return value === 'INGRESO' || value === 'EGRESO' ? value : 'TODOS';
  };

  const [fechaDesde, setFechaDesde] = useState(() => storedString(loadStored(), 'fechaDesde', ''));
  const [fechaHasta, setFechaHasta] = useState(() => storedString(loadStored(), 'fechaHasta', ''));
  const [searchTerm, setSearchTerm] = useState(() => storedString(loadStored(), 'searchTerm', ''));
  const [tipoFiltro, setTipoFiltro] = useState<'TODOS' | 'INGRESO' | 'EGRESO'>(resolveTipoFiltro);
  const [medioPagoFiltro, setMedioPagoFiltro] = useState(() =>
    storedString(loadStored(), 'medioPagoFiltro', '')
  );
  const [currentTab, setCurrentTab] = useState(() => storedNumber(loadStored(), 'currentTab', 0));
  const [mainTab, setMainTab] = useState(() => storedNumber(loadStored(), 'mainTab', 0));
  const [arqueoExpandido, setArqueoExpandido] = useState<Record<string, boolean>>({});

  const [sortField, setSortField] = useState<keyof MovimientoCaja>('nombreMCP');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [selectedMovimiento, setSelectedMovimiento] = useState<MovimientoCaja | null>(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [openArqueoDetalleDialog, setOpenArqueoDetalleDialog] = useState(false);
  const [arqueoDetalleItem, setArqueoDetalleItem] = useState<ArqueoPorTipoPago | null>(null);
  const [openFilters, setOpenFilters] = useState(false);
  const [filtrosAvanzadosExpandidos, setFiltrosAvanzadosExpandidos] = useState(false);
  const { preferidaNro, loading: loadingPreferidaSucursal } = useSucursalesUsuarioContext();
  const { filtroNroSuc, setFiltroNroSuc, sucursales: sucursalesVentaFiltro, loadingSucursales: loadingSucursalesVentaFiltro, filtroListo } = useSucursalesVentaFiltro({
    resetPreferidaOnNavigation: true,
  });

  const [openBuscarCajaDialog, setOpenBuscarCajaDialog] = useState(false);
  const [buscarCajaTerm, setBuscarCajaTerm] = useState('');
  const [buscarCajaResultados, setBuscarCajaResultados] = useState<CajaResumen[]>([]);
  const [buscarCajaEstado, setBuscarCajaEstado] = useState<string>('');
  const [buscarCajaGrupo, setBuscarCajaGrupo] = useState<string>('');
  const [buscarCajaFechaDesde, setBuscarCajaFechaDesde] = useState<string>('');
  const [buscarCajaFechaHasta, setBuscarCajaFechaHasta] = useState<string>('');
  const [buscarCajaPage, setBuscarCajaPage] = useState(0);
  const [buscarCajaRowsPerPage, setBuscarCajaRowsPerPage] = useState(10);
  const [buscarCajaTotal, setBuscarCajaTotal] = useState(0);
  const [buscarCajaLoading, setBuscarCajaLoading] = useState(false);
  const [buscarCajaHaBuscado, setBuscarCajaHaBuscado] = useState(false);
  const [buscarCajaGrupos, setBuscarCajaGrupos] = useState<GrupoCajaChica[]>([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openCierreDialog, setOpenCierreDialog] = useState(false);
  const [cierreGrupoDestino, setCierreGrupoDestino] = useState<number | ''>('');
  const [cierreNroSuc, setCierreNroSuc] = useState<number | ''>('');
  const [loadingCierre, setLoadingCierre] = useState(false);
  const [sucursalesCierre, setSucursalesCierre] = useState<SucursalVenta[]>([]);
  const [gruposCierre, setGruposCierre] = useState<GrupoCajaChica[]>([]);
  const [cajaGrupo, setCajaGrupo] = useState<number | null>(null);

  const [pdfCierreUrl, setPdfCierreUrl] = useState<string | null>(null);
  const [pdfCierreLoading, setPdfCierreLoading] = useState(false);
  const [openDialogFormatoCierrePdf, setOpenDialogFormatoCierrePdf] = useState(false);
  const [pdfCierreDocumentType, setPdfCierreDocumentType] = useState<
    'cierreCaja' | 'cierreCajaIngresoEgreso' | 'cierreCajaPorTipoPago'
  >('cierreCaja');
  const [pdfCierreTitle, setPdfCierreTitle] = useState('');
  const [pdfCierreDownloadName, setPdfCierreDownloadName] = useState('');
  const [pdfMovimientoUrl, setPdfMovimientoUrl] = useState<string | null>(null);
  const [pdfMovimientoId, setPdfMovimientoId] = useState<number | null>(null);
  const [pdfMovimientoFileName, setPdfMovimientoFileName] = useState<string | null>(null);
  const [pdfMovimientoTitle, setPdfMovimientoTitle] = useState<string | null>(null);

  const [openAbrirCajaDialog, setOpenAbrirCajaDialog] = useState(false);
  const [abrirCajaGrupo, setAbrirCajaGrupo] = useState<number | ''>('');
  const [loadingAbrirCaja, setLoadingAbrirCaja] = useState(false);
  const [abrirCajaGrupos, setAbrirCajaGrupos] = useState<GrupoCajaChica[]>([]);

  const cargarInformacionCaja = async (codigo: number, grupoNombreParam?: string | null, grupo?: number): Promise<boolean> => {
    try {
      setLoading(true);
      const tieneAcceso = await tesoreriaCajaService.tieneAccesoACaja(codigo);
      if (!tieneAcceso) {
        setInformacionCaja(null);
        setDetallesCaja(null);
        setGrupoNombre(null);
        try {
          localStorage.removeItem(STORAGE_KEY_CODIGO_CAJA);
        } catch {}
        enqueueSnackbar('No tiene permisos para visualizar esta caja.', { variant: 'warning' });
        return false;
      }
      const info = await tesoreriaCajaService.getInformacionCaja(codigo);
      setInformacionCaja(info);
      try {
        localStorage.setItem(STORAGE_KEY_CODIGO_CAJA, String(codigo));
      } catch {}
      
      // Si se proporciona el grupoNombre como parámetro, usarlo directamente
      if (grupo !== undefined) setCajaGrupo(grupo);
      let nombreGrupoParaStorage = grupoNombreParam ?? null;
      if (grupoNombreParam) {
        setGrupoNombre(grupoNombreParam);
      } else {
        // Buscar el grupo de la caja para obtener el nombre
        try {
          const cajaSearch = await tesoreriaCajaService.searchCajas({
            pageNumber: 1,
            pageSize: 50,
            searchTerm: codigo.toString(),
          });
          const cajaEncontrada = cajaSearch.items.find(c => c.codigoCaja === codigo);
          if (cajaEncontrada?.grupoNombre) {
            setGrupoNombre(cajaEncontrada.grupoNombre);
            nombreGrupoParaStorage = cajaEncontrada.grupoNombre;
          } else {
            setGrupoNombre(null);
          }
        } catch (error) {
          console.error('Error al cargar nombre del grupo:', error);
          setGrupoNombre(null);
        }
      }
      // Persistir grupo para que Traspasos y otras páginas lo sugieran al cargar
      if (grupo !== undefined && grupo != null && Number.isFinite(Number(grupo))) {
        try {
          const toSave = { codigo: Number(grupo), id: Number(grupo), nombre: nombreGrupoParaStorage || `Grupo ${grupo}` };
          localStorage.setItem('grupoCajaChicaSeleccionado', JSON.stringify(toSave));
          localStorage.setItem('grupoCajaMcp', String(grupo));
        } catch {}
      }

      return true;
    } catch (error: any) {
      console.error('Error al cargar información de caja:', error);
      if (error?.response?.status === 404) {
        enqueueSnackbar('Caja no encontrada', { variant: 'error' });
      } else {
        enqueueSnackbar('Error al cargar información de la caja', { variant: 'error' });
      }
      setInformacionCaja(null);
      setGrupoNombre(null);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const cargarDetallesCaja = async (
    codigo: number,
    pageNumber: number = 1,
    pageSize: number = 10,
    fechaDesdeOverride?: string,
    fechaHastaOverride?: string
  ) => {
    try {
      setLoadingDetalles(true);
      const fDesde = fechaDesdeOverride !== undefined ? fechaDesdeOverride : fechaDesde;
      const fHasta = fechaHastaOverride !== undefined ? fechaHastaOverride : fechaHasta;
      // Determinar el tipo de movimiento según el tab seleccionado
      let tipoMovimiento: 'Ingreso' | 'Egreso' | undefined = undefined;
      if (currentTab === 1) {
        tipoMovimiento = 'Ingreso';
      } else if (currentTab === 2) {
        tipoMovimiento = 'Egreso';
      }

      // Cargar solo la página actual del servidor con filtros de tipo y fecha
      const nroSucFiltro = resolveNroSucFiltro(filtroNroSuc);
      const detalles = await tesoreriaCajaService.getDetalleCaja(
        codigo,
        pageNumber,
        pageSize,
        tipoMovimiento,
        fDesde || undefined,
        fHasta || undefined,
        undefined,
        nroSucFiltro
      );
      setDetallesCaja(detalles);

      // Si no estamos en la primera página, cargar todos los movimientos anteriores para calcular el saldo acumulado
      if (pageNumber > 1) {
        const movimientosAnterioresArray: MovimientoCaja[] = [];
        for (let p = 1; p < pageNumber; p++) {
          const response = await tesoreriaCajaService.getDetalleCaja(
            codigo,
            p,
            pageSize,
            tipoMovimiento,
            fDesde || undefined,
            fHasta || undefined,
            undefined,
            nroSucFiltro
          );
          movimientosAnterioresArray.push(...response.items);
        }
        movimientosAnterioresArray.sort(compareMovimientosCajaPorFechaYComprobante);
        setMovimientosAnteriores(movimientosAnterioresArray);
      } else {
        // Si estamos en la primera página, no hay movimientos anteriores
        setMovimientosAnteriores([]);
      }
    } catch (error: any) {
      console.error('Error al cargar detalles de caja:', error);
      if (error?.response?.status === 404) {
        enqueueSnackbar('No se encontraron movimientos para esta caja', { variant: 'warning' });
      } else {
        enqueueSnackbar('Error al cargar los movimientos de la caja', { variant: 'error' });
      }
      setDetallesCaja(null);
      setMovimientosAnteriores([]);
    } finally {
      setLoadingDetalles(false);
    }
  };

  const cargarArqueoCaja = async (codigo: number) => {
    try {
      setLoadingArqueo(true);
      const arqueo = await tesoreriaCajaService.getInformacionArqueo(codigo);
      setArqueoCaja(arqueo);
    } catch (error: any) {
      console.error('Error al cargar arqueo de caja:', error);
      if (error?.response?.status === 404) {
        enqueueSnackbar('No se encontró información de arqueo para esta caja', { variant: 'warning' });
      } else {
        enqueueSnackbar('Error al cargar el arqueo de la caja', { variant: 'error' });
      }
      setArqueoCaja(null);
    } finally {
      setLoadingArqueo(false);
    }
  };

  const parseCodigoCajaCampo = (val: number | ''): number | null => {
    if (val === '' || val == null) return null;
    const n = typeof val === 'number' ? val : Number(val);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
  };

  /** Carga info, arqueo y movimientos al confirmar un código (Enter / blur) o alinear con el botón Actualizar para código nuevo. */
  const handleCommitCodigoCajaDesdeCampo = async (reason: 'enter' | 'blur') => {
    const codigo = parseCodigoCajaCampo(codigoCaja);
    if (!codigo) {
      if (reason === 'enter') {
        enqueueSnackbar('Ingrese un código de caja válido', { variant: 'warning' });
      }
      return;
    }
    if (reason === 'blur' && informacionCaja?.codigoCaja === codigo) {
      return;
    }
    const ok = await cargarInformacionCaja(codigo);
    if (ok) {
      setPage(0);
      setMovimientosAnteriores([]);
      await Promise.all([cargarArqueoCaja(codigo), cargarDetallesCaja(codigo, 1, rowsPerPage)]);
    }
  };

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.movimientosCaja,
    {
      fechaDesde,
      fechaHasta,
      searchTerm,
      tipoFiltro,
      medioPagoFiltro,
      currentTab,
      mainTab,
    },
    [fechaDesde, fechaHasta, searchTerm, tipoFiltro, medioPagoFiltro, currentTab, mainTab]
  );

  // Recargar cuando cambie el tab o los filtros de fecha (resetear a página 1)
  useEffect(() => {
    if (!filtroListo) return;
    if (codigoCaja && typeof codigoCaja === 'number' && informacionCaja) {
      setPage(0);
      setMovimientosAnteriores([]);
      cargarDetallesCaja(codigoCaja, 1, rowsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, fechaDesde, fechaHasta, filtroNroSuc, filtroListo]);

  // Recargar cuando cambie la página
  useEffect(() => {
    if (codigoCaja && typeof codigoCaja === 'number' && informacionCaja && page >= 0) {
      cargarDetallesCaja(codigoCaja, page + 1, rowsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  // Restaurar caja guardada al montar
  useEffect(() => {
    try {
      const s = localStorage.getItem(STORAGE_KEY_CODIGO_CAJA);
      if (!s) return;
      const codigo = parseInt(s, 10);
      if (!Number.isFinite(codigo) || codigo <= 0) return;
      cargarInformacionCaja(codigo).then((ok) => {
        if (ok) {
          cargarArqueoCaja(codigo);
          cargarDetallesCaja(codigo, 1, 10);
        }
      });
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleIrCajaAbierta = async () => {
    try {
      const saved = localStorage.getItem('grupoCajaChicaSeleccionado');
      if (!saved) {
        // Si no hay grupo seleccionado, abrir el buscador filtrado por cajas ABIERTAS
        handleAbrirBuscarCajaDialog('ABIERTO');
        setTimeout(() => {
          ejecutarBusquedaCajas(0, buscarCajaRowsPerPage, 'ABIERTO', '', '');
        }, 0);
        return;
      }

      let grupo: number | null = null;
      try {
        const parsed = JSON.parse(saved);
        grupo = Number(parsed?.codigo ?? parsed?.id ?? null);
      } catch {
        grupo = null;
      }

      if (!grupo || !Number.isFinite(grupo) || grupo <= 0) {
        // Grupo inválido: comportarse como si no hubiera grupo y abrir buscador de cajas abiertas
        handleAbrirBuscarCajaDialog('ABIERTO');
        setTimeout(() => {
          ejecutarBusquedaCajas(0, buscarCajaRowsPerPage, 'ABIERTO', '', '');
        }, 0);
        return;
      }

      const cajaAbierta = await tesoreriaService.getCajaAbiertaPorGrupo(grupo);
      const codigo =
        cajaAbierta?.codigoCaja ??
        cajaAbierta?.nroCaja ??
        cajaAbierta?.codigo ??
        (cajaAbierta as any)?.id ??
        null;

      if (!codigo || Number(codigo) <= 0) {
        enqueueSnackbar('No se encontró una caja abierta para el grupo seleccionado.', {
          variant: 'info',
        });
        return;
      }

      const codigoNum = Number(codigo);
      setCodigoCaja(codigoNum);
      const grupoCaja = cajaAbierta?.grupo ?? grupo ?? null;
      const infoCargada = await cargarInformacionCaja(codigoNum, undefined, grupoCaja ?? undefined);
      // Si la carga fue exitosa, cargar también arqueo y detalles
      if (infoCargada) {
        await Promise.all([
          cargarArqueoCaja(codigoNum),
          cargarDetallesCaja(codigoNum, 1, rowsPerPage)
        ]);
      }
    } catch (error) {
      console.error('Error al ir a caja abierta:', error);
      enqueueSnackbar('Error al buscar la caja abierta', { variant: 'error' });
    }
  };

  const handleAbrirBuscarCajaDialog = (estadoPredefinido?: string) => {
    setOpenBuscarCajaDialog(true);
    setBuscarCajaResultados([]);
    setBuscarCajaHaBuscado(false);
    setBuscarCajaTerm('');
    setBuscarCajaEstado(estadoPredefinido || '');
    setBuscarCajaGrupo('');
    setBuscarCajaFechaDesde('');
    setBuscarCajaFechaHasta('');
    setBuscarCajaPage(0);
    setBuscarCajaTotal(0);
    // Cargar grupos de caja para el filtro
    tesoreriaService
      .getGruposCajaChica()
      .then((grupos) => {
        setBuscarCajaGrupos(grupos || []);
      })
      .catch((error) => {
        console.error('Error al cargar grupos de caja para filtro:', error);
      });
  };

  const handleAbrirBuscarCajaDialogDefault = () => {
    handleAbrirBuscarCajaDialog();
    setTimeout(() => {
      void ejecutarBusquedaCajas(0, buscarCajaRowsPerPage, undefined, '', '');
    }, 0);
  };

  const ejecutarBusquedaCajas = async (
    page: number, 
    pageSize: number,
    estadoOverride?: string,
    fechaDesdeOverride?: string,
    fechaHastaOverride?: string
  ) => {
    const term = buscarCajaTerm.trim();
    try {
      setBuscarCajaLoading(true);
      const response: CajaSearchResponse = await tesoreriaCajaService.searchCajas({
        pageNumber: page + 1,
        pageSize,
        searchTerm: term || undefined,
        estado: estadoOverride !== undefined ? estadoOverride : (buscarCajaEstado || undefined),
        grupo: buscarCajaGrupo ? Number(buscarCajaGrupo) : undefined,
        fechaAperturaDesde: fechaDesdeOverride !== undefined 
          ? (fechaDesdeOverride ? `${fechaDesdeOverride}T00:00:00` : undefined)
          : (buscarCajaFechaDesde ? `${buscarCajaFechaDesde}T00:00:00` : undefined),
        fechaAperturaHasta: fechaHastaOverride !== undefined
          ? (fechaHastaOverride ? `${fechaHastaOverride}T23:59:59` : undefined)
          : (buscarCajaFechaHasta ? `${buscarCajaFechaHasta}T23:59:59` : undefined),
        soloCajasDelUsuario: true,
      });
      setBuscarCajaResultados(response.items);
      setBuscarCajaTotal(response.totalCount);
      setBuscarCajaPage(response.pageNumber - 1);
      setBuscarCajaRowsPerPage(response.pageSize);
      setBuscarCajaHaBuscado(true);
      if (!response.items.length) {
        enqueueSnackbar('No se encontraron cajas con ese criterio', { variant: 'info' });
      }
    } catch (error) {
      console.error('Error al buscar cajas:', error);
      enqueueSnackbar('Error al buscar cajas', { variant: 'error' });
    } finally {
      setBuscarCajaLoading(false);
    }
  };

  const handleBuscarCaja = async () => {
    await ejecutarBusquedaCajas(0, buscarCajaRowsPerPage);
  };

  const handleSeleccionarCaja = async (caja: CajaResumen) => {
    const codigo = caja.codigoCaja;
    setCodigoCaja(codigo);
    setOpenBuscarCajaDialog(false);
    // Cargar información de la caja (valida acceso) pasando el grupoNombre si está disponible
    const infoCargada = await cargarInformacionCaja(codigo, caja.grupoNombre || null, caja.grupo ?? undefined);
    // Si la carga fue exitosa, cargar también arqueo y detalles
    if (infoCargada) {
      await Promise.all([
        cargarArqueoCaja(codigo),
        cargarDetallesCaja(codigo, 1, rowsPerPage)
      ]);
    }
  };

  const handleRefresh = () => {
    if (codigoCaja && typeof codigoCaja === 'number') {
      cargarInformacionCaja(codigoCaja);
      cargarArqueoCaja(codigoCaja);
      cargarDetallesCaja(codigoCaja, page + 1, rowsPerPage);
    }
  };

  const handleVerChequesTercerosEnCartera = () => {
    if (typeof codigoCaja !== 'number' || !informacionCaja) return;
    navigate(
      `/tesoreria/informes/cheques-terceros-cartera?codigoCaja=${codigoCaja}`,
      {
        state: {
          nombreCaja: informacionCaja.nombre ?? arqueoCaja?.nombreCaja ?? undefined,
        },
      }
    );
  };

  const handleClickArqueoTipoPago = (item: ArqueoPorTipoPago) => {
    if (typeof codigoCaja !== 'number' || !informacionCaja) return;
    if (resolveTipoPagoColumnaIdFromCodigo(item.tipoPago) === 'CHEQUE_TERC') {
      handleVerChequesTercerosEnCartera();
      return;
    }
    setArqueoDetalleItem(item);
    setOpenArqueoDetalleDialog(true);
  };

  const handleAbrirCierreDialog = async () => {
    if (!informacionCaja || typeof codigoCaja !== 'number') return;
    setCierreGrupoDestino(cajaGrupo ?? '');
    setCierreNroSuc('');
    const [grupos, suc] = await Promise.all([
      tesoreriaService.getGruposCajaChica().catch(() => []),
      sucursalesVentaService.getSucursalesVenta({ pageNumber: 1, pageSize: 100 }).then(r => r.items).catch(() => []),
      cargarArqueoCaja(codigoCaja),
    ]);
    setGruposCierre(grupos);
    setSucursalesCierre(suc);
    applySucursalPreferidaIfNeeded({
      enabled: !loadingPreferidaSucursal,
      loading: false,
      currentNroSuc: null,
      preferidaNro,
      sucursales: suc,
      onApply: (nroSuc) => setCierreNroSuc(nroSuc),
      force: true,
    });
    setOpenCierreDialog(true);
  };

  const handleCerrarCaja = async () => {
    if (!codigoCaja || typeof codigoCaja !== 'number' || !informacionCaja) return;
    const saldo = informacionCaja.saldoFinal;
    const request: CierreCajaRequest = {
      codigoCaja,
      ...(saldo > 0 && {
        grupoDestino: cierreGrupoDestino === '' ? undefined : cierreGrupoDestino,
        nroSuc: cierreNroSuc === '' ? undefined : cierreNroSuc,
      }),
    };
    if (saldo > 0 && (!request.grupoDestino || !request.nroSuc)) {
      enqueueSnackbar('Cuando la caja tiene saldo positivo debe indicar Grupo destino y Sucursal para la transferencia.', { variant: 'warning' });
      return;
    }
    try {
      setLoadingCierre(true);
      const res = await tesoreriaCajaService.cierreCaja(request);
      if (res.exito) {
        enqueueSnackbar(res.mensaje ?? 'Caja cerrada correctamente.', { variant: 'success' });
        setOpenCierreDialog(false);
        await cargarInformacionCaja(codigoCaja);
        cargarArqueoCaja(codigoCaja);
        cargarDetallesCaja(codigoCaja, 1, rowsPerPage);
      } else {
        enqueueSnackbar(res.mensaje ?? 'Error al cerrar la caja.', { variant: 'error' });
      }
    } catch (err: unknown) {
      if (notifyPermissionDenied(enqueueSnackbar, err)) {
        return;
      }
      const data = (err as { response?: { data?: Record<string, string> } })?.response?.data;
      const msg =
        data?.mensaje ??
        data?.Mensaje ??
        data?.message ??
        'Error al cerrar la caja.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoadingCierre(false);
    }
  };

  const handleAbrirDialogFormatoCierrePdf = () => {
    if (!codigoCaja || typeof codigoCaja !== 'number') return;
    setOpenDialogFormatoCierrePdf(true);
  };

  const handleElegirCierrePdfFormato = async (formato: 'ingresoEgreso' | 'porTipoPago') => {
    if (!codigoCaja || typeof codigoCaja !== 'number') return;
    setOpenDialogFormatoCierrePdf(false);
    try {
      setPdfCierreLoading(true);
      const blob = await tesoreriaCajaService.getCierrePdf(
        codigoCaja,
        formato === 'ingresoEgreso' ? 'ingresoEgreso' : undefined
      );
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      if (formato === 'ingresoEgreso') {
        setPdfCierreDocumentType('cierreCajaIngresoEgreso');
        setPdfCierreTitle(`Informe de cierre (ingreso / egreso) — Caja ${codigoCaja}`);
        setPdfCierreDownloadName(`cierre-caja_${codigoCaja}_ingreso-egreso.pdf`);
      } else {
        setPdfCierreDocumentType('cierreCaja');
        setPdfCierreTitle(`Informe de cierre — Caja ${codigoCaja}`);
        setPdfCierreDownloadName(`cierre-caja_${codigoCaja}.pdf`);
      }
      setPdfCierreUrl(url);
    } catch (err) {
      enqueueSnackbar('Error al generar el informe de cierre.', { variant: 'error' });
    } finally {
      setPdfCierreLoading(false);
    }
  };

  const handleClosePdfCierre = () => {
    if (pdfCierreUrl) window.URL.revokeObjectURL(pdfCierreUrl);
    setPdfCierreUrl(null);
  };

  const handleImprimirMovimientoCaja = async (
    nroFormulario: number,
    nroTipoFormulario?: string
  ) => {
    const pdfConfig = getMovimientoCajaPdfConfig(nroFormulario, nroTipoFormulario);
    try {
      const token = getAccessToken();
      if (!token) {
        enqueueSnackbar('No hay token de autenticación', { variant: 'error' });
        return;
      }
      const response = await api.get(pdfConfig.url, {
        responseType: 'blob',
      });
      const cd = (response.headers as any)?.['content-disposition'] as string | undefined;
      setPdfMovimientoFileName(
        getFileNameFromContentDisposition(cd) ?? pdfConfig.defaultFileName
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPdfMovimientoUrl(url);
      setPdfMovimientoId(nroFormulario);
      setPdfMovimientoTitle(pdfConfig.title);
    } catch (error: any) {
      const status = error.response?.status;
      let message =
        (nroTipoFormulario ?? '').trim() === '4'
          ? 'Error al generar el PDF del recibo'
          : 'Error al generar el PDF del movimiento';
      if (status === 404) {
        message = 'Movimiento no encontrado.';
      } else if (status === 500 && error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = text ? JSON.parse(text) : null;
          if (json?.detail) message = json.detail;
        } catch (_) {
          /* usar mensaje por defecto */
        }
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleClosePdfMovimiento = () => {
    if (pdfMovimientoUrl) window.URL.revokeObjectURL(pdfMovimientoUrl);
    setPdfMovimientoUrl(null);
    setPdfMovimientoId(null);
    setPdfMovimientoFileName(null);
    setPdfMovimientoTitle(null);
  };

  const handleAbrirCajaDialog = async () => {
    setAbrirCajaGrupo('');
    try {
      const grupos = await tesoreriaService.getGruposCajaChica();
      setAbrirCajaGrupos(grupos || []);
    } catch (error) {
      console.error('Error al cargar grupos:', error);
      enqueueSnackbar('Error al cargar grupos MCP', { variant: 'error' });
    }
    setOpenAbrirCajaDialog(true);
  };

  const handleAbrirCaja = async () => {
    if (abrirCajaGrupo === '' || abrirCajaGrupo === null) {
      enqueueSnackbar('Seleccione un grupo MCP', { variant: 'warning' });
      return;
    }
    const grupoNum = typeof abrirCajaGrupo === 'number' ? abrirCajaGrupo : Number(abrirCajaGrupo);
    if (!Number.isFinite(grupoNum) || grupoNum < 0) {
      enqueueSnackbar('Grupo inválido', { variant: 'warning' });
      return;
    }
    const loggedUser =
      localStorage.getItem('userName') ||
      localStorage.getItem('username') ||
      localStorage.getItem('user') ||
      '';
    if (!loggedUser) {
      enqueueSnackbar('No se pudo obtener el usuario logueado', { variant: 'error' });
      return;
    }
    const grupoSeleccionado = abrirCajaGrupos.find((g) => g.codigo === grupoNum || g.id === grupoNum);
    const descripcionRaw = grupoSeleccionado
      ? `Caja ${grupoSeleccionado.nombre} - ${new Date().toLocaleDateString('es-AR')}`
      : `Caja Grupo ${grupoNum} - ${new Date().toLocaleDateString('es-AR')}`;
    const descripcion = descripcionRaw.length > 30 ? descripcionRaw.substring(0, 30) : descripcionRaw;

    try {
      setLoadingAbrirCaja(true);
      const cajaCreada = await tesoreriaCajaService.createCaja({
        descripcion,
        grupo: grupoNum,
        estado: 'Abierto',
        usrLubri: loggedUser,
        fechaApertura: new Date().toISOString(),
      });
      enqueueSnackbar(`Caja abierta correctamente (Código: ${cajaCreada.codigoCaja})`, {
        variant: 'success',
      });
      setOpenAbrirCajaDialog(false);
      setAbrirCajaGrupo('');
      setCodigoCaja(cajaCreada.codigoCaja);
      const infoCargada = await cargarInformacionCaja(cajaCreada.codigoCaja, grupoSeleccionado?.nombre ?? undefined, grupoNum);
      if (infoCargada) {
        await Promise.all([
          cargarArqueoCaja(cajaCreada.codigoCaja),
          cargarDetallesCaja(cajaCreada.codigoCaja, 1, rowsPerPage),
        ]);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data?.mensaje ??
        'Error al abrir la caja. Verifique que no exista ya una caja abierta para este grupo.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoadingAbrirCaja(false);
    }
  };

  const mediosPagoUnicos = useMemo(() => {
    if (!detallesCaja?.items) return [];
    const medios = new Set<string>();
    detallesCaja.items.forEach(item => {
      if (item.nombreMCP) {
        medios.add(item.nombreMCP);
      }
    });
    return Array.from(medios).sort();
  }, [detallesCaja]);

  const movimientosConSaldo = useMemo(() => {
    if (!detallesCaja?.items || !informacionCaja) return [];
    
    const movimientos = [...detallesCaja.items];
    movimientos.sort(compareMovimientosCajaPorFechaYComprobante);
    return attachSaldoAcumuladoDesc(movimientos, informacionCaja.saldoFinal);
  }, [detallesCaja, informacionCaja]);

  const _movimientosFiltrados = useMemo(() => {
    let filtrados = movimientosConSaldo;
    // El filtro de fechas se aplica en el API; los items ya vienen filtrados por fechaDesde/fechaHasta

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtrados = filtrados.filter(m =>
        String(m.nroFormulario).toLowerCase().includes(search) ||
        (m.detalle || '').toLowerCase().includes(search) ||
        (m.usuario || '').toLowerCase().includes(search) ||
        (m.nombre || '').toLowerCase().includes(search) ||
        (m.nombreMCP || '').toLowerCase().includes(search) ||
        (m.nombreTipoFormulario || '').toLowerCase().includes(search)
      );
    }

    if (tipoFiltro !== 'TODOS') {
      filtrados = filtrados.filter(m => 
        m.tipoMovimiento?.toUpperCase() === tipoFiltro.toUpperCase()
      );
    }

    if (medioPagoFiltro) {
      filtrados = filtrados.filter(m => m.nombreMCP === medioPagoFiltro);
    }

    if (sortField) {
      filtrados.sort((a, b) => {
        const aVal: unknown = a[sortField];
        const bVal: unknown = b[sortField];
        if (aVal === undefined || aVal === null) return 1;
        if (bVal === undefined || bVal === null) return -1;
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          // Si son fechas en formato string, convertir a Date para comparar
          if (sortField === 'fechaEmision' || String(sortField).includes('fecha') || String(sortField).includes('Fecha')) {
            const dateA = new Date(aVal).getTime();
            const dateB = new Date(bVal).getTime();
            return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
          }
          const aStr: string = aVal;
          const bStr: string = bVal;
          return sortDirection === 'asc' 
            ? aStr.localeCompare(bStr)
            : bStr.localeCompare(aStr);
        }
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          const aNum: number = aVal;
          const bNum: number = bVal;
          return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
        }
        return 0;
      });
    }

    if (sortField === 'fechaEmision') {
      filtrados.sort((a, b) => {
        const dateA = new Date(a.fechaEmision).getTime();
        const dateB = new Date(b.fechaEmision).getTime();
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      });
    }

    return filtrados;
  }, [movimientosConSaldo, searchTerm, tipoFiltro, medioPagoFiltro, sortField, sortDirection]);

  // Filtrar los movimientos de la página actual (el backend ya filtra por tipo de movimiento según el tab)
  const movimientosPorTab = useMemo(() => {
    if (!detallesCaja?.items || !informacionCaja) return [];
    const items = detallesCaja.items;
    
    // Aplicar filtros de fecha, búsqueda, etc. (el tipo de movimiento ya está filtrado por el backend)
    let filtrados = items;
    
    // El filtro de fechas se aplica en el API; los items ya vienen filtrados por fechaDesde/fechaHasta

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtrados = filtrados.filter(m =>
        m.detalle?.toLowerCase().includes(term) ||
        m.nombre?.toLowerCase().includes(term) ||
        m.nroFormulario?.toString().includes(term) ||
        m.nombreMCP?.toLowerCase().includes(term)
      );
    }
    if (tipoFiltro && tipoFiltro !== 'TODOS') {
      filtrados = filtrados.filter(m => m.tipoMovimiento?.toUpperCase() === tipoFiltro);
    }
    if (medioPagoFiltro && medioPagoFiltro !== 'TODOS') {
      filtrados = filtrados.filter(m => m.nombreMCP === medioPagoFiltro);
    }
    
    // Orden unificado: fecha desc, letra + suc + comprobante desc
    filtrados.sort(compareMovimientosCajaPorFechaYComprobante);

    const previosDesc = [...movimientosAnteriores].sort(compareMovimientosCajaPorFechaYComprobante);
    return attachSaldoAcumuladoDesc(filtrados, informacionCaja.saldoFinal, previosDesc);
  }, [detallesCaja, informacionCaja, movimientosAnteriores, searchTerm, tipoFiltro, medioPagoFiltro]);

  // Total de movimientos según el tab seleccionado
  // Usar totalCount del backend (filtrado por fecha y tipo) cuando hay detalles cargados
  const totalMovimientosTab = useMemo(() => {
    if (detallesCaja) {
      return detallesCaja.totalCount;
    }
    if (!informacionCaja) return 0;
    if (currentTab === 0) {
      return informacionCaja.cantidadMovimientosTotal ?? 0;
    } else if (currentTab === 1) {
      return informacionCaja.cantidadMovimientosIngresos ?? 0;
    } else if (currentTab === 2) {
      return informacionCaja.cantidadMovimientosEgresos ?? 0;
    }
    return 0;
  }, [informacionCaja, detallesCaja, currentTab]);
  const columnasTipoPagoActivas = useMemo(() => {
    const activas = new Set<TipoPagoColumnaId>();
    movimientosPorTab.forEach((mov) => {
      activas.add(getTipoPagoColumnaId(mov));
    });
    return TIPOS_PAGO_COLUMNAS.filter((col) => activas.has(col.id));
  }, [movimientosPorTab]);

  const anchoMinimoGrillaMovimientos = useMemo(
    () => calcAnchoMinimoGrillaMovimientos(columnasTipoPagoActivas.length),
    [columnasTipoPagoActivas.length]
  );

  const _handleSort = (field: keyof MovimientoCaja) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleArqueoExpandido = (key: string) => {
    setArqueoExpandido(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleExportar = async () => {
    if (!codigoCaja || typeof codigoCaja !== 'number' || !informacionCaja) {
      enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      enqueueSnackbar('Obteniendo todos los movimientos para exportar...', { variant: 'info' });

      // Determinar el tipo de movimiento según el tab seleccionado
      let tipoMovimiento: 'Ingreso' | 'Egreso' | undefined = undefined;
      if (currentTab === 1) {
        tipoMovimiento = 'Ingreso';
      } else if (currentTab === 2) {
        tipoMovimiento = 'Egreso';
      }

      // Obtener todos los movimientos paginando (con filtros de fecha aplicados en API)
      const pageSize = 50;
      const nroSucFiltro = resolveNroSucFiltro(filtroNroSuc);
      const firstResponse = await tesoreriaCajaService.getDetalleCaja(
        codigoCaja,
        1,
        pageSize,
        tipoMovimiento,
        fechaDesde || undefined,
        fechaHasta || undefined,
        undefined,
        nroSucFiltro
      );
      const totalMovimientos = firstResponse.totalCount;

      if (totalMovimientos === 0) {
        enqueueSnackbar('No hay movimientos que coincidan con los filtros aplicados', { variant: 'warning' });
        setLoading(false);
        return;
      }

      const allMovimientos: MovimientoCaja[] = [...firstResponse.items];
      const totalPages = Math.ceil(totalMovimientos / pageSize);

      for (let p = 2; p <= totalPages; p++) {
        const response = await tesoreriaCajaService.getDetalleCaja(
          codigoCaja,
          p,
          pageSize,
          tipoMovimiento,
          fechaDesde || undefined,
          fechaHasta || undefined,
          undefined,
          nroSucFiltro
        );
        allMovimientos.push(...response.items);
      }

      // Aplicar filtros del cliente (búsqueda, medio de pago) - fecha ya aplicada en API
      let movimientosFiltrados = allMovimientos;

      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        movimientosFiltrados = movimientosFiltrados.filter(m =>
          String(m.nroFormulario).toLowerCase().includes(search) ||
          (m.detalle || '').toLowerCase().includes(search) ||
          (m.usuario || '').toLowerCase().includes(search) ||
          (m.nombre || '').toLowerCase().includes(search) ||
          (m.nombreMCP || '').toLowerCase().includes(search) ||
          (m.nombreTipoFormulario || '').toLowerCase().includes(search)
        );
      }

      if (medioPagoFiltro) {
        movimientosFiltrados = movimientosFiltrados.filter(m => m.nombreMCP === medioPagoFiltro);
      }

      movimientosFiltrados.sort(compareMovimientosCajaPorFechaYComprobante);

      const movimientosConSaldo = attachSaldoAcumuladoDesc(
        movimientosFiltrados,
        informacionCaja.saldoFinal
      );

      if (movimientosConSaldo.length === 0) {
        enqueueSnackbar('No hay movimientos que coincidan con los filtros aplicados', { variant: 'warning' });
        setLoading(false);
        return;
      }

      // Preparar datos para exportar
      const datos = movimientosConSaldo.map(m => {
        const efecto = signedImporteDisplay(m);
        return {
          'Fecha': formatDate(m.fechaEmision),
          'Tipo': m.nombreTipoFormulario || '',
          'Comprobante': formatComprobante(m),
          'Nombre entidad': m.nombre || '',
          'Medio de Cobro': m.nombreMCP || '',
          'Caja origen': esMovimientoTraspaso(m)
            ? formatCajaPlanillaLabel(m.codigoCajaOrigen, m.nombreCajaOrigen)
            : '',
          'Caja destino': esMovimientoTraspaso(m)
            ? formatCajaPlanillaLabel(m.codigoCajaDestino, m.nombreCajaDestino)
            : '',
          'Detalle': m.detalle || '',
          'Ingreso': efecto > 0 ? efecto : 0,
          'Egreso': efecto < 0 ? Math.abs(efecto) : 0,
          'Saldo': (m as any).saldoAcumulado || 0,
          'Usuario': m.usuario || '',
          'Entidad': m.entidad || 0,
        };
      });

      const ws = XLSX.utils.json_to_sheet(datos);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Movimientos');

      if (informacionCaja) {
        const infoSheet = XLSX.utils.json_to_sheet([{
          'Código de Caja': informacionCaja.codigoCaja,
          'Nombre': informacionCaja.nombre,
          'Estado': informacionCaja.estado,
          'Fecha Apertura': formatDate(informacionCaja.fechaApertura),
          'Fecha Cierre': formatDate(informacionCaja.fechaCierre || ''),
          'Usuario Responsable': informacionCaja.usuarioResponsable,
          'Saldo Inicial': informacionCaja.saldoInicial,
          'Total Ingresos': informacionCaja.totalIngresos,
          'Total Egresos': informacionCaja.totalEgresos,
          'Saldo Final': informacionCaja.saldoFinal,
        }]);
        XLSX.utils.book_append_sheet(wb, infoSheet, 'Información Caja');
      }

      const fileName = `Movimientos_Caja_${informacionCaja?.codigoCaja || 'N'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      enqueueSnackbar(`Datos exportados exitosamente (${movimientosConSaldo.length} movimientos)`, { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar:', error);
      enqueueSnackbar('Error al exportar los datos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };


  const handleLimpiarFiltros = () => {
    setFechaDesde('');
    setFechaHasta('');
    setSearchTerm('');
    setTipoFiltro('TODOS');
    setMedioPagoFiltro('');
    setCurrentTab(0);
  };

  const formatComprobante = (mov: MovimientoCaja) => {
    const comp = formatComprobanteTesoreria(mov.letra, mov.nroSuc, mov.nroComprobante);
    if (comp !== '-') return comp;
    return String(mov.nroFormulario ?? '-');
  };

  const handleClickMovimiento = (movimiento: MovimientoCaja) => {
    setSelectedMovimiento(movimiento);
    setOpenDetails(true);
  };

  const handleFiltrosRapidos = (tipo: 'hoy' | 'semana' | 'mes' | '7dias') => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const finHoy = new Date();
    finHoy.setHours(23, 59, 59, 999);

    switch (tipo) {
      case 'hoy':
        setFechaDesde(hoy.toISOString().split('T')[0]);
        setFechaHasta(hoy.toISOString().split('T')[0]);
        break;
      case 'semana':
        const inicioSemana = new Date(hoy);
        inicioSemana.setDate(hoy.getDate() - hoy.getDay());
        setFechaDesde(inicioSemana.toISOString().split('T')[0]);
        setFechaHasta(finHoy.toISOString().split('T')[0]);
        break;
      case 'mes':
        const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        setFechaDesde(inicioMes.toISOString().split('T')[0]);
        setFechaHasta(finHoy.toISOString().split('T')[0]);
        break;
      case '7dias':
        const hace7Dias = new Date(hoy);
        hace7Dias.setDate(hoy.getDate() - 7);
        setFechaDesde(hace7Dias.toISOString().split('T')[0]);
        setFechaHasta(finHoy.toISOString().split('T')[0]);
        break;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }} mb={3}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCircleIcon />}
              onClick={handleAbrirCajaDialog}
              disabled={loading}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
              }}
            >
              Abrir caja
            </Button>
            <TextField
              label="Código de Caja"
              type="number"
              value={codigoCaja}
              onChange={(e) => setCodigoCaja(e.target.value === '' ? '' : Number(e.target.value))}
              onBlur={() => {
                void handleCommitCodigoCajaDesdeCampo('blur');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleCommitCodigoCajaDesdeCampo('enter');
                }
              }}
              sx={{ flexGrow: { xs: 1, md: 0 }, minWidth: { md: 200 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon
                      sx={{ cursor: 'pointer' }}
                      onMouseDown={(ev) => ev.preventDefault()}
                      onClick={handleAbrirBuscarCajaDialogDefault}
                    />
                  </InputAdornment>
                ),
              }}
            />
            {codigoCaja && (
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                }}
              >
                Actualizar
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={handleIrCajaAbierta}
              disabled={loading}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
              }}
            >
              Ir a caja abierta
            </Button>
            {informacionCaja && (informacionCaja.estado || '').trim().toUpperCase() === 'ABIERTO' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<LockIcon />}
                onClick={handleAbrirCierreDialog}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                }}
              >
                Cerrar caja
              </Button>
            )}
            {informacionCaja && codigoCaja && (
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleAbrirDialogFormatoCierrePdf}
                disabled={loading || pdfCierreLoading}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                }}
              >
                Informe PDF
              </Button>
            )}
          </Stack>

          {informacionCaja && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
                    {informacionCaja.nombre}
                  </Typography>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                      <Typography variant="body1" color="text.secondary">
                        <strong>Código:</strong> {informacionCaja.codigoCaja.toLocaleString('es-AR')}
                      </Typography>
                      {(() => {
                        const estadoRaw = informacionCaja.estado || '';
                        const estadoTrimmed = estadoRaw.trim();
                        const estadoUpper = estadoTrimmed.toUpperCase();
                        const esAbierto = estadoUpper === 'ABIERTO';
                        return (
                          <Chip
                            label={estadoTrimmed}
                            icon={
                              esAbierto
                                ? <LockOpenIcon />
                                : estadoUpper === 'CERRADO'
                                ? <LockIcon />
                                : undefined
                            }
                            size="small"
                            sx={{
                              ...(esAbierto && {
                                bgcolor: '#4caf50 !important',
                                color: '#fff !important',
                                '& .MuiChip-icon': {
                                  color: '#fff !important',
                                },
                              }),
                              ...(!esAbierto && {
                                bgcolor: '#9e9e9e',
                                color: '#fff',
                              }),
                            }}
                          />
                        );
                      })()}
                    </Stack>
                    {grupoNombre && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Grupo:</strong> {grupoNombre}
                      </Typography>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Fecha Apertura:</strong> {formatDate(informacionCaja.fechaApertura)}
                    </Typography>
                    {informacionCaja.fechaCierre && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Fecha Cierre:</strong> {formatDate(informacionCaja.fechaCierre)}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      <AccountCircleIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                      <strong>Usuario:</strong> {informacionCaja.usuarioResponsable}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </>
          )}

          {informacionCaja && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6} sm={4} md={2.4}>
                  <Card sx={{ bgcolor: '#757575', color: '#fff', textAlign: 'center', py: 2 }}>
                    <AccountBalanceIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency(informacionCaja.saldoInicial)}
                    </Typography>
                    <Typography variant="caption">Saldo Inicial</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <Card sx={{ bgcolor: '#4caf50', color: '#fff', textAlign: 'center', py: 2 }}>
                    <TrendingUpIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency(informacionCaja.totalIngresos)}
                    </Typography>
                    <Typography variant="caption">Total Ingresos</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <Card sx={{ bgcolor: '#f44336', color: '#fff', textAlign: 'center', py: 2 }}>
                    <TrendingDownIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency(informacionCaja.totalEgresos)}
                    </Typography>
                    <Typography variant="caption">Total Egresos</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <Card sx={{ bgcolor: '#1976d2', color: '#fff', textAlign: 'center', py: 2 }}>
                    <AccountBalanceIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency(informacionCaja.saldoFinal)}
                    </Typography>
                    <Typography variant="caption">Saldo Final</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <Card sx={{ bgcolor: '#2196f3', color: '#fff', textAlign: 'center', py: 2 }}>
                    <CalculateIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency(informacionCaja.totalIngresos - informacionCaja.totalEgresos)}
                    </Typography>
                    <Typography variant="caption">Diferencia Neta</Typography>
                  </Card>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                {BOTONERA_ITEMS.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <Grid item xs={6} sm={4} md={2.4} key={item.path}>
                      <Box
                        onClick={() => {
                          if (matchesSharedModuleRoute(item.path)) {
                            navigateWithModuleBack(navigate, item.path, '/tesoreria');
                            return;
                          }
                          navigate(item.path);
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                          py: 1.5,
                          px: 1.5,
                          borderRadius: 2,
                          cursor: 'pointer',
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'background.paper',
                          height: '100%',
                          minHeight: 56,
                          '&:hover': {
                            bgcolor: 'action.hover',
                            borderColor: '#2196f3',
                          },
                        }}
                      >
                        <IconComponent sx={{ fontSize: 22, color: '#2196f3', flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', textAlign: 'center' }}>
                          {item.text}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}

        </CardContent>
      </Card>

      {/* Contenedor principal con Tabs grandes */}
      {informacionCaja && (
        <Card sx={{ mt: 3, borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={mainTab}
                onChange={(e, newValue) => setMainTab(newValue)}
                aria-label="tabs principales"
                sx={{
                  '& .MuiTab-root': {
                    fontSize: '1rem',
                    fontWeight: 600,
                    minHeight: 48,
                  },
                }}
              >
                <Tab label="Información de Arqueo" />
                <Tab label="Detalle de Movimientos" />
              </Tabs>
            </Box>

            {/* Tab de Arqueo */}
            {mainTab === 0 && (
              <Box>
                {loadingArqueo ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : arqueoCaja ? (
                  <Box>
                    {arqueoCaja.porTipoPago && arqueoCaja.porTipoPago.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
                          Arqueo por Tipo de Cobro/Pago
                        </Typography>
                        <Grid container spacing={1.5}>
                          {arqueoCaja.porTipoPago.map((item, idx) => {
                            const titulo = getArqueoTipoPagoDisplayLabel(item);
                            const subtitulo =
                              item.descripcion?.trim() && item.tipoPago?.trim() && item.descripcion.trim() !== item.tipoPago.trim()
                                ? item.tipoPago.trim()
                                : null;
                            const { Icon, accent } = getArqueoTipoPagoCardPresentation(item);
                            const esChequeTerc = resolveTipoPagoColumnaIdFromCodigo(item.tipoPago) === 'CHEQUE_TERC';
                            const esClickeable = typeof codigoCaja === 'number' && !!informacionCaja;
                            return (
                              <Grid item xs={6} sm={6} md={4} lg={3} key={`arqueo-tipo-${item.tipoPago}-${idx}`}>
                                <Card
                                  variant="outlined"
                                  role={esClickeable ? 'button' : undefined}
                                  tabIndex={esClickeable ? 0 : undefined}
                                  onClick={esClickeable ? () => handleClickArqueoTipoPago(item) : undefined}
                                  onKeyDown={
                                    esClickeable
                                      ? (e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleClickArqueoTipoPago(item);
                                          }
                                        }
                                      : undefined
                                  }
                                  sx={{
                                    height: '100%',
                                    borderLeft: 4,
                                    borderLeftColor: accent,
                                    borderRadius: 2,
                                    boxShadow: 'none',
                                    ...(esClickeable && {
                                      cursor: 'pointer',
                                      '&:hover': { boxShadow: 1, bgcolor: 'action.hover' },
                                    }),
                                  }}
                                >
                                  <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ minWidth: 0 }}>
                                      <Icon sx={{ color: accent, fontSize: 28, flexShrink: 0, mt: 0.25 }} />
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                          variant="body2"
                                          fontWeight={600}
                                          noWrap
                                          title={titulo}
                                          sx={{ lineHeight: 1.3 }}
                                        >
                                          {titulo}
                                        </Typography>
                                        {subtitulo && (
                                          <Typography variant="caption" color="text.secondary" noWrap title={subtitulo}>
                                            {subtitulo}
                                          </Typography>
                                        )}
                                        <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, lineHeight: 1.2 }}>
                                          {formatCurrency(item.saldo)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Saldo neto
                                        </Typography>
                                        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                                          <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
                                            Ing. {formatCurrency(item.totalIngresos)}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 500 }}>
                                            Egr. {formatCurrency(item.totalEgresos)}
                                          </Typography>
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                          {item.cantidadMovimientos} movimiento{item.cantidadMovimientos !== 1 ? 's' : ''}
                                        </Typography>
                                        {esClickeable && (
                                          <Typography
                                            variant="caption"
                                            color="primary"
                                            display="block"
                                            sx={{ mt: 0.5, fontWeight: 500 }}
                                          >
                                            {esChequeTerc ? 'Ver cheques →' : 'Ver detalle →'}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Stack>
                                  </CardContent>
                                </Card>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    )}

                    {/* Por Tipo de Pago — tabla detalle, contraída por defecto como el resto */}
                    {arqueoCaja.porTipoPago && arqueoCaja.porTipoPago.length > 0 && (() => {
                      const key = 'porTipoPago';
                      const items = arqueoCaja.porTipoPago;
                      const isExpanded = arqueoExpandido[key] || false;
                      return (
                        <Box sx={{ mb: 3 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Arqueo por Tipo de Cobro/Pago
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => toggleArqueoExpandido(key)}
                              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            >
                              {isExpanded ? 'Contraer' : `Ver todos (${items.length})`}
                            </Button>
                          </Stack>
                          {isExpanded && (
                            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                              <Table>
                                <TableHead>
                                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Tipo de Pago</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total Ingresos</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total Egresos</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Cant. Movimientos</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {items.map((item, idx) => (
                                    <TableRow key={idx} hover>
                                      <TableCell>{item.tipoPago?.trim() || '-'}</TableCell>
                                      <TableCell>{item.descripcion?.trim() || '-'}</TableCell>
                                      <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 500 }}>
                                        {formatCurrency(item.totalIngresos)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ color: '#f44336', fontWeight: 500 }}>
                                        {formatCurrency(item.totalEgresos)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                                        {formatCurrency(item.saldo)}
                                      </TableCell>
                                      <TableCell align="right">{item.cantidadMovimientos}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Por MCP */}
                    {arqueoCaja.porMcp && arqueoCaja.porMcp.length > 0 && (() => {
                      const key = 'porMcp';
                      const items = arqueoCaja.porMcp;
                      const isExpanded = arqueoExpandido[key] || false;
                      return (
                        <Box sx={{ mb: 3 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Arqueo por Medio de Cobro/Pago
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => toggleArqueoExpandido(key)}
                              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            >
                              {isExpanded ? 'Contraer' : `Ver todos (${items.length})`}
                            </Button>
                          </Stack>
                          {isExpanded && (
                            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>MCP</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Nombre MCP</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Ingresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Egresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Cant. Movimientos</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {items.map((item, idx) => (
                                  <TableRow key={idx} hover>
                                    <TableCell>{item.mcp?.trim() || '-'}</TableCell>
                                    <TableCell>{item.nombreMcp?.trim() || '-'}</TableCell>
                                    <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 500 }}>
                                      {formatCurrency(item.totalIngresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#f44336', fontWeight: 500 }}>
                                      {formatCurrency(item.totalEgresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      {formatCurrency(item.saldo)}
                                    </TableCell>
                                    <TableCell align="right">{item.cantidadMovimientos}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Por Cartera */}
                    {arqueoCaja.porCartera && arqueoCaja.porCartera.length > 0 && (() => {
                      const key = 'porCartera';
                      const items = arqueoCaja.porCartera;
                      const isExpanded = arqueoExpandido[key] || false;
                      return (
                        <Box sx={{ mb: 3 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Arqueo por Cartera
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => toggleArqueoExpandido(key)}
                              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            >
                              {isExpanded ? 'Contraer' : `Ver todos (${items.length})`}
                            </Button>
                          </Stack>
                          {isExpanded && (
                            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                              <Table>
                                <TableHead>
                                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Cartera</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Nombre Cartera</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total Ingresos</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total Egresos</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Cant. Movimientos</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {items.map((item, idx) => (
                                    <TableRow key={idx} hover>
                                      <TableCell>{item.cartera}</TableCell>
                                      <TableCell>{item.nombreCartera?.trim() || '-'}</TableCell>
                                      <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 500 }}>
                                        {formatCurrency(item.totalIngresos)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ color: '#f44336', fontWeight: 500 }}>
                                        {formatCurrency(item.totalEgresos)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                                        {formatCurrency(item.saldo)}
                                      </TableCell>
                                      <TableCell align="right">{item.cantidadMovimientos}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Por Tipo de Formulario */}
                    {arqueoCaja.porTipoFormulario && arqueoCaja.porTipoFormulario.length > 0 && (() => {
                      const key = 'porTipoFormulario';
                      const items = arqueoCaja.porTipoFormulario;
                      const isExpanded = arqueoExpandido[key] || false;
                      return (
                        <Box sx={{ mb: 3 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Arqueo por Tipo de Formulario
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => toggleArqueoExpandido(key)}
                              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            >
                              {isExpanded ? 'Contraer' : `Ver todos (${items.length})`}
                            </Button>
                          </Stack>
                          {isExpanded && (
                            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Tipo Formulario</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Nombre Tipo Formulario</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Ingresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Egresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Cant. Movimientos</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {items.map((item, idx) => (
                                  <TableRow key={idx} hover>
                                    <TableCell>{item.tipoFormulario?.trim() || '-'}</TableCell>
                                    <TableCell>{item.nombreTipoFormulario?.trim() || '-'}</TableCell>
                                    <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 500 }}>
                                      {formatCurrency(item.totalIngresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#f44336', fontWeight: 500 }}>
                                      {formatCurrency(item.totalEgresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      {formatCurrency(item.saldo)}
                                    </TableCell>
                                    <TableCell align="right">{item.cantidadMovimientos}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Por Usuario */}
                    {arqueoCaja.porUsuario && arqueoCaja.porUsuario.length > 0 && (() => {
                      const key = 'porUsuario';
                      const items = arqueoCaja.porUsuario;
                      const isExpanded = arqueoExpandido[key] || false;
                      return (
                        <Box sx={{ mb: 3 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Arqueo por Usuario
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => toggleArqueoExpandido(key)}
                              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            >
                              {isExpanded ? 'Contraer' : `Ver todos (${items.length})`}
                            </Button>
                          </Stack>
                          {isExpanded && (
                            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Ingresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Egresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Cant. Movimientos</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {items.map((item, idx) => (
                                  <TableRow key={idx} hover>
                                    <TableCell>{item.usuario?.trim() || '-'}</TableCell>
                                    <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 500 }}>
                                      {formatCurrency(item.totalIngresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#f44336', fontWeight: 500 }}>
                                      {formatCurrency(item.totalEgresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      {formatCurrency(item.saldo)}
                                    </TableCell>
                                    <TableCell align="right">{item.cantidadMovimientos}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Por Entidad */}
                    {arqueoCaja.porEntidad && arqueoCaja.porEntidad.length > 0 && (() => {
                      const key = 'porEntidad';
                      const items = arqueoCaja.porEntidad;
                      const isExpanded = arqueoExpandido[key] || false;
                      return (
                        <Box sx={{ mb: 3 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Arqueo por Entidad
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => toggleArqueoExpandido(key)}
                              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            >
                              {isExpanded ? 'Contraer' : `Ver todos (${items.length})`}
                            </Button>
                          </Stack>
                          {isExpanded && (
                            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Entidad</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Ingresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Egresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Cant. Movimientos</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {items.map((item, idx) => (
                                  <TableRow key={idx} hover>
                                    <TableCell>{item.entidad}</TableCell>
                                    <TableCell>{item.nombre?.trim() || '-'}</TableCell>
                                    <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 500 }}>
                                      {formatCurrency(item.totalIngresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#f44336', fontWeight: 500 }}>
                                      {formatCurrency(item.totalEgresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      {formatCurrency(item.saldo)}
                                    </TableCell>
                                    <TableCell align="right">{item.cantidadMovimientos}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Por Fecha */}
                    {arqueoCaja.porFecha && arqueoCaja.porFecha.length > 0 && (() => {
                      const key = 'porFecha';
                      const items = arqueoCaja.porFecha;
                      const isExpanded = arqueoExpandido[key] || false;
                      return (
                        <Box>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Arqueo por Fecha
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => toggleArqueoExpandido(key)}
                              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            >
                              {isExpanded ? 'Contraer' : `Ver todos (${items.length})`}
                            </Button>
                          </Stack>
                          {isExpanded && (
                            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Ingresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Egresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Cant. Movimientos</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {items.map((item, idx) => (
                                  <TableRow key={idx} hover>
                                    <TableCell>{formatDate(item.fechaEmision)}</TableCell>
                                    <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 500 }}>
                                      {formatCurrency(item.totalIngresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#f44336', fontWeight: 500 }}>
                                      {formatCurrency(item.totalEgresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      {formatCurrency(item.saldo)}
                                    </TableCell>
                                    <TableCell align="right">{item.cantidadMovimientos}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    No hay información de arqueo disponible
                  </Typography>
                )}
              </Box>
            )}

            {/* Tab de Movimientos */}
            {mainTab === 1 && (
              <Box>
                {/* Filtros */}
                <Accordion
                  expanded={filtrosAvanzadosExpandidos}
                  onChange={(_, expanded) => setFiltrosAvanzadosExpandidos(expanded)}
                  sx={{ mb: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#f9f9f9' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <FilterAltIcon sx={{ color: '#1976d2' }} />
                      <Typography variant="h6" sx={{ color: '#1976d2' }}>
                        Filtros Avanzados
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                  <Grid container spacing={2} alignItems="center">
                    {/* Sección de Fechas */}
                    <Grid item xs={12} md={4}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <TextField
                          label="Fecha Desde"
                          type="date"
                          value={fechaDesde}
                          onChange={(e) => setFechaDesde(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          sx={{ flex: 1, minWidth: 140 }}
                        />
                        <TextField
                          label="Fecha Hasta"
                          type="date"
                          value={fechaHasta}
                          onChange={(e) => setFechaHasta(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          sx={{ flex: 1, minWidth: 140 }}
                        />
                      </Stack>
                    </Grid>

                    {/* Botones de Filtros Rápidos */}
                    <Grid item xs={12} md={4}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={() => handleFiltrosRapidos('hoy')}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          HOY
                        </Button>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={() => handleFiltrosRapidos('semana')}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          ESTA SEMANA
                        </Button>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={() => handleFiltrosRapidos('mes')}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          ESTE MES
                        </Button>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={() => handleFiltrosRapidos('7dias')}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          7 DÍAS
                        </Button>
                      </Stack>
                    </Grid>

                    {/* Búsqueda */}
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Buscar"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FiltroSucursalVentaSelect
                        value={filtroNroSuc}
                        onChange={(v) => {
                          setFiltroNroSuc(v);
                          setPage(0);
                        }}
                        loading={loadingSucursalesVentaFiltro}
                        sucursales={sucursalesVentaFiltro}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>

                    {/* Filtros de Tipo y Medio de Pago */}
                    <Grid item xs={12} md={6}>
                      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
                        <SearchableSelect
                          label="Tipo"
                          size="small"
                          sx={{ minWidth: { xs: '100%', sm: 150 } }}
                          options={[...TIPO_FILTRO_CAJA_OPTIONS]}
                          value={tipoFiltro}
                          onChange={(value) => {
                            if (value) setTipoFiltro(value as 'TODOS' | 'INGRESO' | 'EGRESO');
                          }}
                        />
                        {mediosPagoUnicos.length > 0 && (
                          <Autocomplete
                            {...searchableSelectAutocompleteProps}
                            options={['', ...mediosPagoUnicos]}
                            getOptionLabel={(option) => option || 'Todos'}
                            value={medioPagoFiltro}
                            onChange={(_, newValue) => setMedioPagoFiltro(newValue ?? '')}
                            isOptionEqualToValue={(opt, val) => opt === val}
                            filterOptions={(options, { inputValue }) => {
                              if (!inputValue?.trim()) return options;
                              const term = inputValue.trim().toLowerCase();
                              return options.filter((opt) => (opt ?? '').toLowerCase().includes(term));
                            }}
                            sx={{
                              flex: 1,
                              minWidth: { xs: '100%', sm: 280 },
                              '& .MuiOutlinedInput-root': {
                                minHeight: 56,
                                fontSize: '1rem',
                                padding: '8px 14px',
                              },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Medio de Pago"
                                placeholder="Buscar..."
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#666', fontSize: 22 }} />
                                      </InputAdornment>
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            renderOption={(props, option) => (
                              <Box component="li" {...props} key={option || 'todos'} sx={{ py: 1.5 }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <AccountBalanceIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                                  <Typography variant="body1" fontWeight={500}>
                                    {option || 'Todos'}
                                  </Typography>
                                </Stack>
                              </Box>
                            )}
                          />
                        )}
                      </Stack>
                    </Grid>

                    {/* Botones de Acción */}
                    <Grid item xs={12} md={6}>
                      <Stack 
                        direction="row" 
                        spacing={1} 
                        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<FilterListIcon />}
                          onClick={() => setOpenFilters(!openFilters)}
                        >
                          FILTRAR
                        </Button>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={handleLimpiarFiltros}
                        >
                          LIMPIAR
                        </Button>
                        <Button 
                          size="small" 
                          variant="contained" 
                          color="primary"
                          startIcon={<GetAppIcon />} 
                          onClick={handleExportar}
                        >
                          EXPORTAR
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Tabs de movimientos (Todos/Ingresos/Egresos) */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs
                    value={currentTab}
                    onChange={(e, newValue) => setCurrentTab(newValue)}
                    aria-label="tabs de movimientos"
                  >
                    <Tab
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>Todos</span>
                          <Chip
                            label={informacionCaja?.cantidadMovimientosTotal ?? 0}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Stack>
                      }
                    />
                    <Tab
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <ArrowUpwardIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                          <span>Ingresos</span>
                          <Chip
                            label={informacionCaja?.cantidadMovimientosIngresos ?? 0}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Stack>
                      }
                    />
                    <Tab
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <ArrowDownwardIcon sx={{ fontSize: 16, color: '#f44336' }} />
                          <span>Egresos</span>
                          <Chip
                            label={informacionCaja?.cantidadMovimientosEgresos ?? 0}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Stack>
                      }
                    />
                  </Tabs>
                </Box>

                {/* Contenido de movimientos */}
                {loadingDetalles ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {isMobile ? (
                      <Stack spacing={2}>
                        {movimientosPorTab.map((mov, idx) => {
                          const esIngreso = mov.tipoMovimiento?.toUpperCase() === 'INGRESO';
                          return (
                            <Card
                              key={`${mov.nroFormulario}-${idx}`}
                              variant="outlined"
                              sx={{
                                borderLeftWidth: 3,
                                borderLeftStyle: 'solid',
                                borderLeftColor: esIngreso ? 'success.main' : 'error.main',
                                bgcolor: 'background.paper',
                                '&:hover': { boxShadow: 2, cursor: 'pointer', bgcolor: 'action.hover' },
                              }}
                              onClick={() => handleClickMovimiento(mov)}
                            >
                              <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
                                <Stack spacing={0.75}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                    <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                                      {formatDate(mov.fechaEmision)}
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Tooltip title="Imprimir PDF">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleImprimirMovimientoCaja(mov.nroFormulario, mov.nroTipoFormulario);
                                        }}
                                        sx={{
                                          bgcolor: '#2196f3',
                                          color: '#fff',
                                          borderRadius: 1.5,
                                          '&:hover': { bgcolor: '#1976d2' }
                                        }}
                                        aria-label="Imprimir movimiento"
                                      >
                                        <PrintIcon sx={{ fontSize: 18 }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Chip
                                      label={mov.tipoMovimiento}
                                      size="small"
                                      variant="outlined"
                                      color={esIngreso ? 'success' : 'error'}
                                      sx={{ height: 22, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                                      icon={esIngreso ? <ArrowUpwardIcon sx={{ fontSize: 12 }} /> : <ArrowDownwardIcon sx={{ fontSize: 12 }} />}
                                    />
                                    </Stack>
                                  </Stack>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.35 }}>
                                    <strong>Medio:</strong> {mov.nombreMCP || '-'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.35 }}>
                                    <strong>Comprobante:</strong> {formatComprobante(mov)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.35 }}>
                                    <strong>Entidad:</strong> {mov.nombre || '-'}
                                  </Typography>
                                  {esMovimientoTraspaso(mov) && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.35 }}>
                                      <strong>Planillas:</strong>{' '}
                                      {formatCajaPlanillaLabel(mov.codigoCajaOrigen, mov.nombreCajaOrigen)}
                                      {' → '}
                                      {formatCajaPlanillaLabel(mov.codigoCajaDestino, mov.nombreCajaDestino)}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.35 }}>
                                    {mov.detalle || '-'}
                                  </Typography>
                                  <Grid container spacing={0.75} sx={{ pt: 0.25 }}>
                                    {columnasTipoPagoActivas.length > 0 &&
                                      columnasTipoPagoActivas.map((col) => {
                                        const tipoPagoColumna = getTipoPagoColumnaId(mov);
                                        const mostrarImporte = tipoPagoColumna === col.id;
                                        return (
                                          <Grid item xs={6} key={col.id}>
                                            <Typography variant="caption" display="block" fontWeight={600} sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                              {col.label}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontSize: '0.75rem',
                                                color: mostrarImporte ? (esIngreso ? 'success.main' : 'error.main') : 'text.disabled',
                                              }}
                                            >
                                              {mostrarImporte ? formatCurrency(signedImporteDisplay(mov)) : '—'}
                                            </Typography>
                                          </Grid>
                                        );
                                      })}
                                    <Grid item xs={12}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        Saldo: {formatCurrency((mov as any).saldoAcumulado || 0)}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Stack>
                              </CardContent>
                            </Card>
                          );
                        })}
                        {movimientosPorTab.length === 0 && (
                          <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                            No hay movimientos para mostrar
                          </Typography>
                        )}
                        <TablePagination
                          component="div"
                          count={totalMovimientosTab}
                          page={page}
                          onPageChange={(e, newPage) => setPage(newPage)}
                          rowsPerPage={rowsPerPage}
                          onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                          }}
                          rowsPerPageOptions={[10]}
                          labelRowsPerPage="Filas por página"
                          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                        />
                      </Stack>
                    ) : (
                      <>
                        <TableContainer
                          component={Paper}
                          sx={{
                            borderRadius: 2,
                            boxShadow: 'none',
                            border: '1px solid #e0e0e0',
                            width: '100%',
                            maxWidth: '100%',
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                          }}
                        >
                          <Table
                            sx={{
                              minWidth: anchoMinimoGrillaMovimientos,
                              width: 'max-content',
                              tableLayout: 'fixed',
                              '& .MuiTableCell-root': {
                                fontSize: '0.8125rem',
                                lineHeight: 1.45,
                                py: 1,
                                verticalAlign: 'top',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                              },
                              '& thead .MuiTableCell-root': { py: 1.25, whiteSpace: 'nowrap' },
                            }}
                          >
                            <TableHead>
                              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                <TableCell sx={sxGrillaMovCajaCol('fecha', { fontWeight: 600 })}>
                                  Fecha / Comprobante
                                </TableCell>
                                <TableCell sx={sxGrillaMovCajaCol('tipoEntidad', { fontWeight: 600 })}>
                                  Tipo / Entidad
                                </TableCell>
                                <TableCell sx={sxGrillaMovCajaCol('medioCobro', { fontWeight: 600 })}>
                                  Medio de Cobro / Detalle
                                </TableCell>
                                <TableCell sx={sxGrillaMovCajaCol('cajaTraspaso', { fontWeight: 600 })}>
                                  Caja origen / destino
                                </TableCell>
                                {columnasTipoPagoActivas.map((col) => (
                                  <TableCell
                                    key={col.id}
                                    align="right"
                                    sx={sxGrillaMovCajaCol('tipoPago', { fontWeight: 600, color: 'text.secondary' })}
                                  >
                                    {col.label}
                                  </TableCell>
                                ))}
                                <TableCell align="right" sx={sxGrillaMovCajaCol('saldo', { fontWeight: 600 })}>
                                  Saldo
                                </TableCell>
                                <TableCell align="center" sx={sxGrillaMovCajaCol('imprimir', { fontWeight: 600 })}>
                                  Imprimir
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {movimientosPorTab.map((mov, idx) => {
                                const esIngreso = mov.tipoMovimiento?.toUpperCase() === 'INGRESO';
                                return (
                                  <TableRow
                                    key={`${mov.nroFormulario}-${idx}`}
                                    sx={{
                                      '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' },
                                    }}
                                    onClick={() => handleClickMovimiento(mov)}
                                  >
                                    <TableCell sx={sxGrillaMovCajaCol('fecha')}>
                                      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                        <Box component="span" sx={{ whiteSpace: 'nowrap', lineHeight: 1.35 }}>
                                          {formatDate(mov.fechaEmision)}
                                        </Box>
                                        <GrillaTextoConTooltip
                                          texto={formatComprobante(mov)}
                                          sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                                        />
                                      </Stack>
                                    </TableCell>
                                    <TableCell sx={sxGrillaMovCajaCol('tipoEntidad')}>
                                      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                        <GrillaTextoConTooltip
                                          texto={mov.nombreTipoFormulario || '-'}
                                          sx={{ fontWeight: 500, lineHeight: 1.35 }}
                                        />
                                        <GrillaTextoConTooltip
                                          texto={mov.nombre || '-'}
                                          sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                                        />
                                      </Stack>
                                    </TableCell>
                                    <TableCell sx={sxGrillaMovCajaCol('medioCobro')}>
                                      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                        <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ minWidth: 0 }}>
                                          {esIngreso ? (
                                            <ArrowUpwardIcon sx={{ fontSize: 14, color: 'success.main', flexShrink: 0, mt: 0.15 }} />
                                          ) : (
                                            <ArrowDownwardIcon sx={{ fontSize: 14, color: 'error.main', flexShrink: 0, mt: 0.15 }} />
                                          )}
                                          <GrillaTextoConTooltip texto={mov.nombreMCP || '-'} sx={{ minWidth: 0, flex: 1 }} />
                                        </Stack>
                                        <GrillaTextoConTooltip
                                          texto={mov.detalle || '-'}
                                          sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                                        />
                                      </Stack>
                                    </TableCell>
                                    <TableCell sx={sxGrillaMovCajaCol('cajaTraspaso')}>
                                      {esMovimientoTraspaso(mov) ? (
                                        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                          <GrillaTextoConTooltip
                                            texto={`Origen: ${formatCajaPlanillaLabel(mov.codigoCajaOrigen, mov.nombreCajaOrigen)}`}
                                            sx={{ fontWeight: 500, lineHeight: 1.35 }}
                                          />
                                          <GrillaTextoConTooltip
                                            texto={`Destino: ${formatCajaPlanillaLabel(mov.codigoCajaDestino, mov.nombreCajaDestino)}`}
                                            sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                                          />
                                        </Stack>
                                      ) : (
                                        '—'
                                      )}
                                    </TableCell>
                                    {columnasTipoPagoActivas.map((col) => {
                                      const tipoPagoColumna = getTipoPagoColumnaId(mov);
                                      const mostrarImporte = tipoPagoColumna === col.id;
                                      return (
                                        <TableCell
                                          key={col.id}
                                          align="right"
                                          sx={sxGrillaMovCajaCol('tipoPago', {
                                            fontVariantNumeric: 'tabular-nums',
                                            whiteSpace: 'nowrap',
                                          })}
                                        >
                                          {mostrarImporte ? (
                                            <Box component="span" sx={{ color: esIngreso ? 'success.main' : 'error.main', fontWeight: 500 }}>
                                              {formatCurrency(signedImporteDisplay(mov))}
                                            </Box>
                                          ) : (
                                            <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
                                          )}
                                        </TableCell>
                                      );
                                    })}
                                    <TableCell
                                      align="right"
                                      sx={sxGrillaMovCajaCol('saldo', {
                                        fontWeight: 500,
                                        fontVariantNumeric: 'tabular-nums',
                                        whiteSpace: 'nowrap',
                                      })}
                                    >
                                      {formatCurrency((mov as any).saldoAcumulado || 0)}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      onClick={(e) => e.stopPropagation()}
                                      sx={sxGrillaMovCajaCol('imprimir', { verticalAlign: 'middle' })}
                                    >
                                      <Tooltip title="Imprimir PDF">
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleImprimirMovimientoCaja(mov.nroFormulario, mov.nroTipoFormulario)
                                          }
                                          sx={{
                                            p: 0.5,
                                            bgcolor: '#2196f3',
                                            color: '#fff',
                                            borderRadius: 1,
                                            '&:hover': { bgcolor: '#1976d2' },
                                          }}
                                          aria-label="Imprimir movimiento"
                                        >
                                          <PrintIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                          {movimientosPorTab.length === 0 && (
                            <Box sx={{ py: 4 }}>
                              <Typography align="center" color="text.secondary">
                                No hay movimientos para mostrar
                              </Typography>
                            </Box>
                          )}
                        </TableContainer>
                        <TablePagination
                          component="div"
                          count={totalMovimientosTab}
                          page={page}
                          onPageChange={(e, newPage) => setPage(newPage)}
                          rowsPerPage={rowsPerPage}
                          onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                          }}
                          rowsPerPageOptions={[10]}
                          labelRowsPerPage="Filas por página"
                          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                        />
                      </>
                    )}
                  </>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Detalles del Movimiento
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedMovimiento && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Número de Formulario:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.nroFormulario}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Tipo de Formulario:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.nombreTipoFormulario || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Fecha y Hora:</strong>
                  </Typography>
                  <Typography variant="body1">{formatDate(selectedMovimiento.fechaEmision)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Tipo de Movimiento:</strong>
                  </Typography>
                  <Chip
                    label={selectedMovimiento.tipoMovimiento}
                    sx={{
                      bgcolor: selectedMovimiento.tipoMovimiento?.toUpperCase() === 'INGRESO' ? '#4caf50' : '#f44336',
                      color: '#fff',
                      mt: 0.5
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Usuario:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.usuario || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Entidad:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.entidad || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Medio de Cobro/Pago:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.nombreMCP || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Tipo de Pago:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.tipoPago || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Cartera:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.nombreCartera || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Importe:</strong>
                  </Typography>
                  <Typography variant="h6" sx={{ color: selectedMovimiento.tipoMovimiento?.toUpperCase() === 'INGRESO' ? '#4caf50' : '#f44336' }}>
                    {formatCurrency(signedImporteDisplay(selectedMovimiento))}
                  </Typography>
                </Grid>
                {movimientoTieneDatosCheque(selectedMovimiento) && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Banco:</strong>
                      </Typography>
                      <Typography variant="body1">{selectedMovimiento.nombreBancoCheque?.trim() || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Número de Cheque:</strong>
                      </Typography>
                      <Typography variant="body1">{formatNumeroChequeMovimiento(selectedMovimiento)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>CUIT/CUIL del emisor:</strong>
                      </Typography>
                      <Typography variant="body1">{selectedMovimiento.nroLibrador?.trim() || '-'}</Typography>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Detalle:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.detalle || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Nombre:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.nombre || '-'}</Typography>
                </Grid>
                {(selectedMovimiento as any).saldoAcumulado !== undefined && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Saldo Acumulado:</strong>
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency((selectedMovimiento as any).saldoAcumulado || 0)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de cierre de caja */}
      <Dialog
        open={openCierreDialog}
        onClose={() => !loadingCierre && setOpenCierreDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ py: 1.5, fontSize: '1.05rem' }}>Cerrar caja</DialogTitle>
        <DialogContent dividers sx={{ py: 1.5 }}>
          {informacionCaja && (
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
                  Arqueo contable por tipo de pago
                </Typography>
                {loadingArqueo ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : arqueoCaja?.porTipoPago && arqueoCaja.porTipoPago.length > 0 ? (
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ borderRadius: 1, maxHeight: 200 }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            Saldo
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            Cant. mov.
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {arqueoCaja.porTipoPago.map((item, idx) => (
                          <TableRow key={`${item.tipoPago}-${idx}`} hover>
                            <TableCell>
                              {getArqueoTipoPagoDisplayLabel(item)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 600,
                                color: item.saldo < 0 ? 'error.main' : 'text.primary',
                              }}
                            >
                              {formatCurrency(item.saldo)}
                            </TableCell>
                            <TableCell align="right">{item.cantidadMovimientos}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay datos de arqueo por tipo de pago para esta caja.
                  </Typography>
                )}
              </Box>

              <Typography variant="body2">
                Saldo actual:{' '}
                <Box
                  component="strong"
                  sx={{
                    color: informacionCaja.saldoFinal < 0 ? 'error.main' : 'text.primary',
                  }}
                >
                  {formatCurrency(informacionCaja.saldoFinal)}
                </Box>
              </Typography>

              {informacionCaja.saldoFinal > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    La caja tiene saldo positivo. Indique el grupo de destino y la sucursal para generar los traspasos.
                  </Typography>
                  <SearchableSelect
                    label="Grupo destino"
                    size="small"
                    allowEmpty
                    emptyOptionLabel="Seleccione..."
                    options={gruposCierre.map((g) => ({ value: g.codigo, label: g.nombre }))}
                    value={cierreGrupoDestino === '' ? null : cierreGrupoDestino}
                    onChange={(value) => setCierreGrupoDestino(value ?? '')}
                  />
                  <SearchableSelect
                    label="Sucursal (NroSuc)"
                    size="small"
                    allowEmpty
                    emptyOptionLabel="Seleccione..."
                    options={sucursalesCierre.map((s) => ({
                      value: s.nroSuc,
                      label: `${s.nroSuc} - ${s.nombreSucursal}`,
                    }))}
                    value={cierreNroSuc === '' ? null : cierreNroSuc}
                    onChange={(value) => setCierreNroSuc(value ?? '')}
                  />
                </>
              )}
              {informacionCaja.saldoFinal === 0 && (
                <Typography variant="body2" color="text.secondary">
                  La caja se cerrará sin generar traspasos.
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ py: 1 }}>
          <Button size="small" onClick={() => setOpenCierreDialog(false)} disabled={loadingCierre}>
            Cancelar
          </Button>
          <Button size="small" variant="contained" onClick={handleCerrarCaja} disabled={loadingCierre}>
            {loadingCierre ? <CircularProgress size={22} /> : 'Confirmar cierre'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para abrir una nueva caja */}
      <Dialog
        open={openAbrirCajaDialog}
        onClose={() => !loadingAbrirCaja && setOpenAbrirCajaDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Abrir caja</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Seleccione el grupo MCP para abrir la caja. Solo se muestran los grupos asociados a su usuario.
            </Typography>
            <SearchableSelect
              label="Grupo MCP"
              allowEmpty
              emptyOptionLabel="Seleccione un grupo..."
              required
              options={abrirCajaGrupos.map((g) => ({ value: g.codigo, label: g.nombre }))}
              value={abrirCajaGrupo === '' ? null : abrirCajaGrupo}
              onChange={(value) => setAbrirCajaGrupo(value ?? '')}
            />
            {abrirCajaGrupos.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No tiene grupos MCP asociados. Contacte al administrador.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAbrirCajaDialog(false)} disabled={loadingAbrirCaja}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAbrirCaja}
            disabled={loadingAbrirCaja || abrirCajaGrupo === ''}
          >
            {loadingAbrirCaja ? <CircularProgress size={24} /> : 'Abrir caja'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de búsqueda de cajas */}
      <Dialog
        open={openBuscarCajaDialog}
        onClose={() => setOpenBuscarCajaDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Buscar caja</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Buscar por código o nombre"
                  value={buscarCajaTerm}
                  onChange={(e) => setBuscarCajaTerm(e.target.value)}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SearchableSelect
                  label="Estado"
                  size="small"
                  allowEmpty
                  emptyOptionLabel="Todos"
                  options={[...ESTADO_CAJA_OPTIONS]}
                  value={buscarCajaEstado || null}
                  onChange={(value) => setBuscarCajaEstado(value ?? '')}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SearchableSelect
                  label="Grupo"
                  size="small"
                  allowEmpty
                  emptyOptionLabel="Todos"
                  options={buscarCajaGrupos.map((g) => ({
                    value: String(g.codigo),
                    label: `${g.codigo} - ${g.nombre}`,
                  }))}
                  value={buscarCajaGrupo || null}
                  onChange={(value) => setBuscarCajaGrupo(value ?? '')}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Fecha Apertura desde"
                  type="date"
                  value={buscarCajaFechaDesde}
                  onChange={(e) => setBuscarCajaFechaDesde(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Fecha Apertura hasta"
                  type="date"
                  value={buscarCajaFechaHasta}
                  onChange={(e) => setBuscarCajaFechaHasta(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <Button
              variant="contained"
              onClick={handleBuscarCaja}
              disabled={buscarCajaLoading}
              sx={{ alignSelf: 'flex-start' }}
            >
              {buscarCajaLoading ? <CircularProgress size={22} /> : 'Buscar'}
            </Button>
            {buscarCajaLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={80}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Código</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Fecha Apertura</TableCell>
                      <TableCell>Fecha Cierre</TableCell>
                      <TableCell>Grupo</TableCell>
                      <TableCell>Nombre Grupo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {buscarCajaResultados.map((caja) => (
                      <TableRow
                        key={caja.codigoCaja}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleSeleccionarCaja(caja)}
                      >
                        <TableCell>{caja.codigoCaja.toLocaleString('es-AR')}</TableCell>
                        <TableCell>{caja.descripcion}</TableCell>
                        <TableCell>{caja.estado || '-'}</TableCell>
                        <TableCell>{caja.fechaApertura ? formatDate(caja.fechaApertura) : '-'}</TableCell>
                        <TableCell>{caja.fechaCierre ? formatDate(caja.fechaCierre) : '-'}</TableCell>
                        <TableCell>{caja.grupo ?? '-'}</TableCell>
                        <TableCell>{caja.grupoNombre || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {!buscarCajaResultados.length && !buscarCajaLoading && (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography variant="body2" color="text.secondary">
                            {buscarCajaHaBuscado
                              ? 'No se encontraron cajas con los filtros seleccionados. Pruebe ampliar el rango de fechas o quitar filtros.'
                              : 'Presione "Buscar" para listar las cajas a las que tiene acceso.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[5, 10, 20]}
                  count={buscarCajaTotal}
                  rowsPerPage={buscarCajaRowsPerPage}
                  page={buscarCajaPage}
                  onPageChange={(_, newPage) => {
                    ejecutarBusquedaCajas(newPage, buscarCajaRowsPerPage);
                  }}
                  onRowsPerPageChange={(e) => {
                    const newSize = parseInt(e.target.value, 10);
                    setBuscarCajaRowsPerPage(newSize);
                    ejecutarBusquedaCajas(0, newSize);
                  }}
                  labelRowsPerPage="Filas por página"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                  }
                />
              </TableContainer>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBuscarCajaDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialogFormatoCierrePdf} onClose={() => setOpenDialogFormatoCierrePdf(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Informe PDF de cierre</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            El informe principal usa columnas por medio de pago. Puede abrir también otra opcion con columnas de ingreso y egreso.
          </Typography>
          <Stack spacing={1.5}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disabled={pdfCierreLoading}
              onClick={() => void handleElegirCierrePdfFormato('porTipoPago')}
            >
              Columnas de tipo de pago
            </Button>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              disabled={pdfCierreLoading}
              onClick={() => void handleElegirCierrePdfFormato('ingresoEgreso')}
            >
              Columnas de Ingreso / Egreso 
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialogFormatoCierrePdf(false)} disabled={pdfCierreLoading}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {typeof codigoCaja === 'number' && arqueoDetalleItem && (
        <ArqueoTipoPagoDetalleDialog
          open={openArqueoDetalleDialog}
          onClose={() => {
            setOpenArqueoDetalleDialog(false);
            setArqueoDetalleItem(null);
          }}
          codigoCaja={codigoCaja}
          item={arqueoDetalleItem}
          titulo={getArqueoTipoPagoDisplayLabel(arqueoDetalleItem)}
          nombreCaja={informacionCaja?.nombre ?? arqueoCaja?.nombreCaja ?? undefined}
          onVerMovimiento={handleClickMovimiento}
        />
      )}

      {pdfCierreUrl && codigoCaja && typeof codigoCaja === 'number' && (
        <PDFViewer
          pdfUrl={pdfCierreUrl}
          onClose={handleClosePdfCierre}
          title={pdfCierreTitle || `Informe de cierre — Caja ${codigoCaja}`}
          documentId={`${codigoCaja}`}
          documentType={pdfCierreDocumentType}
          downloadFileName={pdfCierreDownloadName || `cierre-caja_${codigoCaja}.pdf`}
        />
      )}

      {pdfMovimientoUrl && pdfMovimientoId != null && (
        <PDFViewer
          pdfUrl={pdfMovimientoUrl}
          onClose={handleClosePdfMovimiento}
          title={pdfMovimientoTitle ?? `Movimiento de caja ${pdfMovimientoId}`}
          documentId={`${pdfMovimientoId}`}
          downloadFileName={
            pdfMovimientoFileName ?? `movimiento-caja_${pdfMovimientoId}.pdf`
          }
        />
      )}
    </Box>
  );
};

export default MovimientosCaja;

